import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { Chart, ChartDataset } from "chart.js";

import { numero, pct } from "../bsc-formato";
import { COLOR_BSC, GraficaBsc } from "./grafica-bsc";

const TRIMESTRES = ["Q1", "Q2", "Q3", "Q4"];

/**
 * BSC · acumulado por trimestre (docs/BSC.md §7 gráfica 3): barras con el
 * real acumulado de Q1..Q4 (E_q en A1, A_q en A2, L_q en A3), línea de raya
 * larga con el programado acumulado y, si se da, línea punteada con el
 * objetivo Solser de cada trimestre. Un real nulo (después del corte) no
 * pinta barra; el programado y el objetivo se ven completos porque son lo
 * planeado. A2 y A3 (4.3) la reutilizan con la serie del área o del KPI.
 * T2 (F7.1) le agrega `comparado`: las barras del año de referencia, en gris,
 * a la izquierda de las del año que se compara.
 */
@Component({
  selector: "app-acumulado-q-bsc",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bsc-acumulado">
      <canvas #lienzo role="img" [attr.aria-label]="textoAccesible"></canvas>
    </div>
    <ul class="bsc-grafica-leyenda">
      <li *ngIf="hayComparado"><i class="bsc-grafica-leyenda__barra is-comparado"></i>{{ nombreComparado }}</li>
      <li><i class="bsc-grafica-leyenda__barra is-real"></i>{{ nombreReal }}</li>
      <li><i class="bsc-grafica-leyenda__linea"></i>{{ nombreProgramado }}</li>
      <li *ngIf="hayObjetivo"><i class="bsc-grafica-leyenda__objetivo"></i>{{ nombreObjetivo }}</li>
    </ul>
  `,
  styleUrls: ["./grafica-bsc.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcumuladoQBscComponent extends GraficaBsc {
  /** Real acumulado Q1..Q4 (fracciones); nulo después del corte. */
  @Input() real: (number | null)[] | null | undefined = [];
  /** Programado acumulado Q1..Q4. */
  @Input() programado: (number | null)[] | null | undefined = [];
  /** Objetivo Solser de cada trimestre; vacío o nulo = sin línea. */
  @Input() objetivo: (number | null)[] | null | undefined = null;
  /** T2: el real acumulado del año de referencia (barras grises); vacío o nulo = sin ellas. */
  @Input() comparado: (number | null)[] | null | undefined = null;
  @Input() nombreComparado = "Año anterior";
  @Input() nombreReal = "Real acumulado";
  @Input() nombreProgramado = "Programado acumulado";
  @Input() nombreObjetivo = "Objetivo Solser";
  @Input() descripcion = "Alcance acumulado por trimestre";

  get hayObjetivo(): boolean {
    return this.serie(this.objetivo).some((v) => v != null);
  }

  get hayComparado(): boolean {
    return this.serie(this.comparado).some((v) => v != null);
  }

  get textoAccesible(): string {
    const real = this.serie(this.real);
    const programado = this.serie(this.programado);
    const objetivo = this.serie(this.objetivo);
    const comparado = this.serie(this.comparado);
    const hayComparado = this.hayComparado;
    const detalle = TRIMESTRES.map((q, i) => {
      const meta = objetivo[i] != null ? `, objetivo ${pct(objetivo[i])}` : "";
      const antes = hayComparado ? `${this.nombreComparado} ${pct(comparado[i], "sin dato")}, ` : "";
      return `${q}: ${antes}${hayComparado ? this.nombreReal : "real"} ${pct(real[i], "sin dato")}, programado ${pct(programado[i])}${meta}`;
    }).join("; ");
    return `${this.descripcion}. ${detalle}.`;
  }

  /** Siempre 4 posiciones; lo que no se puede leer, nulo. */
  private serie(valores: (number | null)[] | null | undefined): (number | null)[] {
    return TRIMESTRES.map((_, i) => numero(valores?.[i]));
  }

  protected arma(lienzo: HTMLCanvasElement): Chart | null {
    const real = this.serie(this.real);
    const programado = this.serie(this.programado);
    const objetivo = this.serie(this.objetivo);
    const comparado = this.serie(this.comparado);
    const maximo = Math.max(1, ...[...real, ...programado, ...objetivo, ...comparado].map((v) => v ?? 0));
    const colorProgramado = this.color(COLOR_BSC.programado);
    const colorObjetivo = this.color(COLOR_BSC.objetivo);

    const series: ChartDataset<"bar" | "line", (number | null)[]>[] = [];
    if (comparado.some((v) => v != null)) {
      series.push({
        type: "bar",
        label: this.nombreComparado,
        data: comparado,
        backgroundColor: this.color(COLOR_BSC.comparado),
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: "bottom",
        maxBarThickness: 46,
        order: 3,
      });
    }
    series.push(
      {
        type: "bar",
        label: this.nombreReal,
        data: real,
        backgroundColor: this.color(COLOR_BSC.real),
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: "bottom",
        maxBarThickness: 46,
        order: 2,
      },
      {
        type: "line",
        label: this.nombreProgramado,
        data: programado,
        borderColor: colorProgramado,
        backgroundColor: colorProgramado,
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 4,
        tension: 0,
        order: 1,
      },
    );
    if (objetivo.some((v) => v != null)) {
      series.push({
        type: "line",
        label: this.nombreObjetivo,
        data: objetivo,
        borderColor: colorObjetivo,
        backgroundColor: colorObjetivo,
        borderDash: [2, 3],
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
        stepped: "middle",
        order: 0,
      });
    }

    return new Chart<"bar" | "line", (number | null)[], string>(lienzo, {
      type: "bar",
      data: { labels: TRIMESTRES, datasets: series },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            ticks: { color: this.color(COLOR_BSC.suave), font: { size: 11 } },
            grid: { display: false },
            border: { color: this.color(COLOR_BSC.rejilla) },
          },
          y: {
            min: 0,
            max: maximo,
            ticks: { color: this.color(COLOR_BSC.suave), font: { size: 11 }, callback: (v) => pct(Number(v)), maxTicksLimit: 5 },
            grid: { color: this.color(COLOR_BSC.rejilla) },
            border: { display: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (c) => `${c.dataset.label}: ${pct(c.raw as number | null, "sin dato")}`,
            },
          },
        },
      },
    });
  }
}
